

import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import Footer from "../components/Footer";
import { useNavigate } from "react-router-dom";

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [paymentDetails, setPaymentDetails] = useState({
    amount: "",
    date: "",
    reference: "",
    status: "pending",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(180); // 3 minutes = 180 seconds
  const [redirectCountdown, setRedirectCountdown] = useState(10); // 10 seconds for successful payments

  const getUrlParams = () => {
    const hash = window.location.hash;
    const queryString = hash.includes("?") ? hash.split("?")[1] : "";
    return new URLSearchParams(queryString);
  };

  // Fetch details from backend using referenceFromUrl
  useEffect(() => {
    const fetchPaymentDetails = async () => {
      try {
        const urlParams = getUrlParams();
        const referenceFromUrl = urlParams.get("reference");

        if (!referenceFromUrl) {
          throw new Error("No reference number found in URL");
        }

        const response = await fetch(
          `${process.env.REACT_APP_HOST_API_URL}/payment/payment-details/`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ payment_request_id: referenceFromUrl }),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch payment details");
        }

        const res = await response.json();
        const data = res.data || {}; 

        setPaymentDetails({
          amount: `$${data.amount}`,
          date: data.created_at
            ? new Date(data.created_at).toLocaleString()
            : "-",
          reference: data.reference_number || referenceFromUrl,
          status: ["succeeded", "completed", "success"].includes(
            (data.status || "").toLowerCase()
          )
            ? "success"
            : "failed",
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    // Call immediately on mount
    fetchPaymentDetails();

     // Then poll every 10 seconds
    const interval = setInterval(fetchPaymentDetails, 10000);

    // Cleanup on unmount
    return () => clearInterval(interval);
    
  }, []);

  // Countdown timer effect
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          // After 3 minutes, redirect to profile
          navigate('/profile');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  // Redirect countdown for successful payments
  useEffect(() => {
    if (paymentDetails.status === "success") {
      const redirectTimer = setInterval(() => {
        setRedirectCountdown((prev) => {
          if (prev <= 1) {
            // After 10 seconds, redirect to profile
            navigate('/profile');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(redirectTimer);
    }
  }, [paymentDetails.status, navigate]);

  // Helper function to format countdown time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) return <p className="text-center mt-5">Loading...</p>;

  const isSuccess = paymentDetails.status === "success";
  const colorClass = isSuccess ? "text-success" : "text-warning";
  const btnClass = isSuccess ? "btn-success" : "btn-warning";

  return (
    <div className="d-flex flex-column min-vh-100" >
      <main className="d-flex flex-column align-items-center justify-content-center flex-grow-1 text-center p-5">
        <CircleCheckIcon
          className={`${colorClass} mb-3`}
          style={{ width: "64px", height: "64px" }}
          success={isSuccess}
        />
        <h1 className={`fw-bold fs-3 ${colorClass}`}>
          {isSuccess ? "Payment Successful" : "Payment Pending"}
        </h1>
        
        {/* Countdown Messages */}
        {isSuccess ? (
          <div className="mt-3">
            <p className="text-muted mb-2">Redirecting to profile in:</p>
            <h2 className="text-muted fw-bold">{redirectCountdown} seconds</h2>
          </div>
        ) : (
          <div className="mt-3">
            <p className="text-muted mb-2">We are processing your payment.</p>
            <p className="text-muted mb-2">If you don't see the status updated, please refresh the page.</p>
            <p className="text-muted mb-2">Auto-redirect in:</p>
            <h2 className="text-warning fw-bold">{formatTime(countdown)}</h2>
          </div>
        )}

        <div
          className="card shadow mt-4"
          style={{ maxWidth: "400px", width: "100%" }}
        >
          <div className="card-body text-start">
            <div className="d-flex justify-content-between small mb-2 gap-4">
              <span>Amount Paid:</span>
              <span className="fw-medium">{paymentDetails.amount}</span>
            </div>
            <div className="d-flex justify-content-between small mb-2 gap-4">
              <span>Date &amp; Time:</span>
              <span className="fw-medium">{paymentDetails.date}</span>
            </div>
            <div className="d-flex justify-content-between small gap-4">
              <span>Rf No:</span>
              <span className="fw-medium">{paymentDetails.reference}</span>
            </div>
          </div>
        </div>

        <a onClick={() => navigate('/profile')} className={`btn mt-4 px-4 ${btnClass}`}>
          Return to Homepage
        </a>
      </main>

      <Footer />
    </div>
  );
};

/* ✅ Success / Failure Icon */
function CircleCheckIcon({ className, style, success }) {
  return (
    <svg
      className={className}
      style={style}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke={success ? "currentColor" : "orange"}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      {success ? <path d="m9 12 2 2 4-4" /> : <path d="M9 9l6 6M15 9l-6 6" />}
    </svg>
  );
}

export default PaymentSuccess;

